
/*eslint-disable no-magic-numbers*/

const defaultInterpolationSteps = 50


const smoothRatio = 2.8 // 2.8 is an arbitrary choice to have a nice curve when params.smooth is 1



function Bezier ({
    origin,
    end,
    controlOrigin,
    controlEnd,
    interpolationSteps = defaultInterpolationSteps
}) {
    this.stepsCount        = interpolationSteps
    this.interpolationStep = 1 / interpolationSteps

    this.origin        = origin
    this.end           = end
    this.controlOrigin = controlOrigin
    this.controlEnd    = controlEnd
}


Bezier.pointsWithDefaultControlPoints = function ({points: originalPoints, smooth = 1, close}) {

    smooth /= smoothRatio

    let points = clonePoints(originalPoints, close)

    let count = points.length
    if (count < 1) {
        return false
    }

    for (let i = 1; i < count - 1; i++) {
        addControlPointsFor(points[i], points[i - 1], points[i + 1], smooth)
    }

    if (close) {
        addControlPointsFor(points[count - 1], points[count - 2], points[1], smooth)
        setDefaultValue(points[0], "controlNext", points[count - 1].controlNext)
    }

    return points
}



Bezier.autoParamsFromPoints = function ({points, smooth = 0, close}) {
    if (smooth) {
        points = Bezier.pointsWithDefaultControlPoints({points, smooth, close})
    }
    let params = []
    let count  = points.length
    for (let j = 1; j < count; j++) {
        const {x: xA, y: yA, controlNext}     = points[j - 1]
        const {x: xB, y: yB, controlPrevious} = points[j]
        params.push({
            origin:        {x: xA, y: yA},
            controlOrigin: controlNext && {
                x: xA + controlNext.x,
                y: yA + controlNext.y
            },
            end:           {x: xB, y: yB},
            controlEnd:    controlPrevious && {
                x: xB + controlPrevious.x,
                y: yB + controlPrevious.y
            }
        })
    }
    return params
}





Bezier.prototype.length = function () {
    if (!("computedLength" in this)) {
        analyzeDistances(this)
    }
    return this.computedLength
}


Bezier.prototype.boundingBox = function () {
    if (!("computedBoundingBox" in this)) {
        analyzeDistances(this)
    }
    return this.computedBoundingBox
}



Bezier.prototype.coords = function (distance, {withAngle = false} = {}) {
    if (!("distanceTable" in this)) {
        createDistanceTable(this)
    }
    let t = getTFromDistance(this, distance)
    return this.parametricCoords(t, {withAngle})
}


const deltaT = 0.00001

Bezier.prototype.parametricCoords = function (t, {withAngle = false} = {}) {
    const ts = 1 - t

    const coef1 = ts * ts * ts
    const coef2 = ts * ts * t * 3
    const coef3 = ts * t  * t * 3
    const coef4 = t  * t  * t

    let x = coef1 * this.origin.x +
            coef2 * this.controlOrigin.x +
            coef3 * this.controlEnd.x +
            coef4 * this.end.x

    let y = coef1 * this.origin.y +
            coef2 * this.controlOrigin.y +
            coef3 * this.controlEnd.y +
            coef4 * this.end.y

    if (withAngle) {
        let closePoint = this.parametricCoords(t + deltaT)
        let angle = Math.atan2(closePoint.y - y, closePoint.x - x)
        return {x, y, angle}
    } else {
        return {x, y}
    }

}


Bezier.prototype.path = function (stepsCount = this.stepsCount) {
    const step = 1 / stepsCount
    const tMax = 1 + step / 2

    const length = Math.ceil(tMax / step)
    const points = new Array(length)
    for (let i = 0; i < length; i += 1) {
        points[i] = this.parametricCoords(i * step)
    }
    return points
}


Bezier.prototype.steppedPath = function ({distanceStepsCount = this.stepsCount} = {}) {
    if (!("tDistributedDistanceTable" in this)) {
        analyzeDistances(this)
    }

    const {origin, interpolationStep, computedLength, tDistributedDistanceTable, stepsCount} = this

    const samplingDistance = computedLength / distanceStepsCount
    let targetDistance     = samplingDistance

    const result = [{x: origin.x, y: origin.y}]

    let previousDist = 0
    for (let tIndex = 0; tIndex < stepsCount; tIndex++) {
        let dist = tDistributedDistanceTable[tIndex + 1]
        while (dist >= targetDistance) {
            const t = (tIndex + (targetDistance - previousDist) / (dist - previousDist)) * interpolationStep
            result.push(this.parametricCoords(t))
            targetDistance = samplingDistance * result.length
        }
        previousDist = dist
    }

    return result
}


Bezier.prototype.derivative = function (t) {

    const t2    = 3 * t * t
    const coef1 = 6 * t - 3 - t2
    const coef2 = 3 * t2 - 12 * t + 3
    const coef3 = 6 * t - 3 * t2

    const x = coef1 * this.origin.x +
              coef2 * this.controlOrigin.x +
              coef3 * this.controlEnd.x +
              t2    * this.end.x

    const y = coef1 * this.origin.y +
              coef2 * this.controlOrigin.y +
              coef3 * this.controlEnd.y +
              t2    * this.end.y

    return {x, y}

}


Bezier.prototype.secondDerivative = function (t) {

    const t6 = 6 * t
    const coef1 = 6 - t6
    const coef2 = 3 * t6 - 12
    const coef3 = 6 - 3 * t6

    const x = coef1 * this.origin.x +
              coef2 * this.controlOrigin.x +
              coef3 * this.controlEnd.x +
              t6    * this.end.x

    const y = coef1 * this.origin.y +
              coef2 * this.controlOrigin.y +
              coef3 * this.controlEnd.y +
              t6    * this.end.y

    return {x, y}

}


Bezier.prototype.normal = function (t) {
    const {x, y} = this.derivative(t)
    const norm   = Math.sqrt(x * x + y * y)
    return {
        x: -y / norm,
        y: x  / norm
    }
}

Bezier.prototype.paramAtDistance = function (distance) {
    if (!("distanceTable" in this)) {
        createDistanceTable(this)
    }
    return getTFromDistance(this, distance)
}


function getTFromDistance (bezier, distance) {

    const {computedLength, distanceTable, tDistributedDistanceTable, interpolationStep} = bezier

    if (distance >= computedLength) {
        return 1
    }

    const distanceIndex = Math.floor(distance / (interpolationStep * computedLength))

    let tIndex = distanceTable[distanceIndex]

    let distA  = tDistributedDistanceTable[tIndex]
    let distB  = tDistributedDistanceTable[tIndex + 1]

    while (distB < distance) {
        tIndex += 1
        distA = distB
        distB = tDistributedDistanceTable[tIndex + 1]
    }
    return (tIndex + (distance - distA) / (distB - distA)) * interpolationStep

}



function createDistanceTable (bezier) {

    if (!("tDistributedDistanceTable" in bezier)) {
        analyzeDistances(bezier)
    }

    const {interpolationStep, computedLength, tDistributedDistanceTable, stepsCount} = bezier

    const samplingDistance = computedLength * interpolationStep

    const distanceTable  = [0]
    let targetDistance = samplingDistance

    for (let index = 0; index < stepsCount; index++) {
        let dist = tDistributedDistanceTable[index + 1]
        while (dist >= targetDistance) {
            distanceTable.push(index)
            targetDistance += samplingDistance
        }
    }

    bezier.distanceTable = distanceTable

}




function analyzeDistances (bezier) {
    let distance  = 0
    const tDistributedDistanceTable = []
    let previousCoords

    let xMin =  Infinity
    let xMax = -Infinity
    let yMin =  Infinity
    let yMax = -Infinity

    const {interpolationStep} = bezier
    const tMax = 1 + interpolationStep / 2

    for (let t = 0; t <= tMax; t += interpolationStep) {
        const coords = bezier.parametricCoords(t)
        const {x, y} = coords
        xMin = Math.min(xMin, x)
        xMax = Math.max(xMax, x)
        yMin = Math.min(yMin, y)
        yMax = Math.max(yMax, y)
        if (previousCoords) {
            distance += distanceBetween(previousCoords, coords)
        }
        tDistributedDistanceTable.push(distance)
        previousCoords = coords
    }

    bezier.computedLength      = distance
    bezier.computedBoundingBox = {xMin, yMin, xMax, yMax}
    bezier.tDistributedDistanceTable = tDistributedDistanceTable
}





function addControlPointsFor (point, prevPoint, nextPoint, smooth) { //eslint-disable-line complexity
    if (point.controlPrevious && point.controlNext) {
        return
    }

    const dx1   = point.x - prevPoint.x
    const dy1   = point.y - prevPoint.y
    const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)

    const dx2   = nextPoint.x - point.x
    const dy2   = nextPoint.y - point.y
    const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)

    if (dist1 === 0 || dist2 === 0) {
        setDefaultValue(point, "controlPrevious", {x: 0, y: 0})
        setDefaultValue(point, "controlNext",     {x: 0, y: 0})
    } else {
        const ratio = dist1 / dist2
        let dx      = dx2 * ratio + dx1
        let dy      = dy2 * ratio + dy1
        const dist  = Math.sqrt(dx * dx + dy * dy)
        dx /= dist
        dy /= dist

        if (!point.controlPrevious) {
            point.controlPrevious = {
                x: -smooth * dx * dist1,
                y: -smooth * dy * dist1
            }
        }

        if (!point.controlNext) {
            point.controlNext = {
                x: smooth * dx * dist2,
                y: smooth * dy * dist2
            }
        }

    }
}







//********************************************** UTILITIES


function clonePoints (points, close) {

    const cloned = points.map(clonePoint)

    if (close && points.length > 0) {
        cloned.push(clonePoint(points[0]))
    }

    return cloned
}


function clonePoint ({x, y, controlNext, controlPrevious}) {
    return {x, y, controlNext, controlPrevious}
}


function setDefaultValue (object, property, value) {
    object[property] = object[property] || value
}


function distanceBetween ({x, y}, {x: xB, y: yB}) {
    const dx = xB - x
    const dy = yB - y
    return Math.sqrt(dx * dx + dy * dy)
}



export default Bezier


